import Image from 'next/image';

import { Box, Flex, StyleProps, Text } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { ContentContainer } from '~/components/container';
import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { PrimaryButtonLink } from '~/components/primary-button-link';

export type FooterBannerSectionProps = {
  title: string;
  subTitle?: string | null;
  ctaLabel?: string | null;
  ctaLink?: string | null;
  theme?: 'shapes' | 'lines' | null;
  onCtaClick?: () => void;
};

export function FooterBannerSection({
  title,
  subTitle,
  ctaLabel,
  ctaLink,
  theme,
  onCtaClick,
}: FooterBannerSectionProps) {
  function getThemeHeadingStyles(): Record<'headingEmphasis' | 'heading' | 'subheading' | 'container', StyleProps> {
    switch (theme) {
      default:
      case 'shapes':
        return {
          headingEmphasis: {},
          heading: {
            fontWeight: '500 !important',
          },
          subheading: {
            fontSize: {
              base: 'marketing-md',
              md: 'marketing-lg',
            },
            fontFamily: {
              base: 'body',
              md: 'heading',
            },
            maxWidth: '814px',
            margin: '0 auto',
          },
          container: {
            bgGradient: 'linear(180deg, #272196 0%, #4037F5 100%)',
            color: 'white',
            textAlign: 'center',
          },
        };
      case 'lines':
        return {
          headingEmphasis: {
            color: 'blue.400',
            fontWeight: '500',
          },
          heading: {},
          subheading: {
            fontSize: {},
            fontFamily: {},
            maxWidth: '814px',
            margin: '0 auto',
          },
          container: {
            textAlign: 'center',
            bgGradient: 'linear(180deg, #F2F6FF 0%, #C6DAF8 100%)',
          },
        };
    }
  }

  const headingStyles = getThemeHeadingStyles();

  function renderButton() {
    if (!ctaLabel || !ctaLink) {
      return null;
    }

    if (theme === 'shapes') {
      return <PrimaryButtonLink href={ctaLink} label={ctaLabel} width='min-content' onClick={onCtaClick} />;
    }

    return (
      <ButtonLink
        linkProps={{ href: ctaLink }}
        buttonProps={{
          variant: 'solid',
          colorScheme: 'blue',
          size: 'icon-right',
          rightIcon: <ArrowRightIcon />,
          onClick: onCtaClick,
        }}
      >
        {ctaLabel}
      </ButtonLink>
    );
  }

  return (
    <Box
      {...headingStyles.container}
      as='section'
      overflow='hidden'
      position='relative'
      padding={{
        base: '40px 0px',
        md: '80px 0px',
      }}
    >
      {theme === 'lines' && (
        <Box
          height='1000px'
          left='50%'
          opacity={0.2}
          position='absolute'
          top='0%'
          transform='translate(-50%, -50%)'
          width='2880px'
        >
          <Image
            alt=''
            quality={100}
            sizes='2880px'
            src='/images/backgrounds/blue-lines-wide.png'
            style={{
              objectFit: 'cover',
              objectPosition: 'left center',
              pointerEvents: 'none',
            }}
            fill
          />
        </Box>
      )}
      <ContentContainer position='relative'>
        <Flex
          alignItems='center'
          flexDirection='column'
          gap={{
            base: 6,
            md: 8,
          }}
        >
          {theme === 'shapes' && (
            <>
              <Box
                height='899px'
                position='absolute'
                transform='rotate(150deg)'
                width='400px'
                left={{
                  base: '-262px',
                  md: '-30%',
                }}
                opacity={{
                  base: 0.5,
                  md: 1,
                }}
                top={{
                  base: '-34px',
                  md: '-300px',
                }}
              >
                <Image
                  alt=''
                  loading='lazy'
                  sizes='400px'
                  src='/images/backgrounds/floating-shapes-3.png'
                  style={{
                    objectFit: 'contain',
                    pointerEvents: 'none',
                  }}
                  fill
                />
              </Box>
              <Box
                height='899px'
                position='absolute'
                width='400px'
                opacity={{
                  base: 0.5,
                  md: 1,
                }}
                right={{
                  base: '-309px',
                  md: '-30%',
                }}
                top={{
                  base: '-422px',
                  md: '-300px',
                }}
                transform={{
                  base: 'rotate(185deg)',
                  md: 'rotate(90deg)',
                }}
              >
                <Image
                  alt=''
                  loading='lazy'
                  sizes='400px'
                  src='/images/backgrounds/floating-shapes-2.png'
                  style={{
                    objectFit: 'contain',
                    pointerEvents: 'none',
                  }}
                  fill
                />
              </Box>
            </>
          )}
          <Flex direction='column' gap={2} position='relative'>
            <HeadingWithEmphasis
              text={title}
              emphasisProps={{
                ...headingStyles.headingEmphasis,
              }}
              headingProps={{
                ...headingStyles.heading,
                as: 'h2',
                size: {
                  base: 'marketing-3xl',
                  md: 'marketing-5xl',
                },
                whiteSpace: {
                  base: 'normal',
                  md: 'pre-wrap',
                },
                textAlign: 'center',
              }}
            />
            {!!subTitle && <Text {...headingStyles.subheading}>{subTitle}</Text>}
          </Flex>
          {renderButton()}
        </Flex>
      </ContentContainer>
    </Box>
  );
}
